"use client";

import { rpc } from "#rpc";
import {
	ScrollAreaBar,
	ScrollAreaRoot,
	ScrollAreaViewport,
} from "@/components/ui/ScrollArea";
import { Skeleton } from "@/components/ui/Skeleton";
import { Text } from "@/components/ui/Text";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Suspense, useEffect, useMemo } from "react";
import styles from "./index.module.css";
import { RecentlyViewedCard } from "./RecentlyViewedCard";
import { useRecentlyViewed } from "./store";

export interface RecentlyViewedProps {
	currentSku: string;
}

export const RecentlyViewed = ({ currentSku: currentSku }: RecentlyViewedProps) => {
	const t = useTranslations("products");
	return (
		<section className={styles.root}>
			<Text data-layout="page" className={styles.title} variant="h3" bold>
				{t("recentlyViewed")}
			</Text>
			<Suspense fallback={<RecentlyViewedSkeleton />}>
				<UnsuspendedRecentlyViewedList currentSku={currentSku} />
			</Suspense>
		</section>
	);
};

const UnsuspendedRecentlyViewedList = ({ currentSku }: { currentSku: string }) => {
	const { products: productIds, addProduct } = useRecentlyViewed();

	useEffect(() => {
		addProduct(currentSku);
	}, [currentSku, addProduct]);

	const sliced = useMemo(() => productIds.slice(0, 6), [productIds]);

	const { data: products, isLoading } = useQuery({
		queryKey: ["recently-viewed", sliced],
		queryFn: async () => {
			return rpc.products.byIds(sliced);
		},
	});

	if (isLoading) {
		return <RecentlyViewedSkeleton />;
	}

	return (
		<ScrollAreaRoot className={styles.scrollarea} data-layout="page">
			<ScrollAreaViewport>
				<div className={styles.list}>
					{products?.map((product) => {
						return (
							<RecentlyViewedCard
								key={product.id}
								href={`/product/${product.uri}`}
								name={product.name}
								image={product.media.at(0)?.href}
							/>
						);
					})}
				</div>
			</ScrollAreaViewport>
			<ScrollAreaBar forceMount orientation="horizontal" />
		</ScrollAreaRoot>
	);
};

const RecentlyViewedSkeleton = () => {
	return (
		<ScrollAreaRoot className={styles.scrollarea} data-layout="page">
			<ScrollAreaViewport>
				<div className={styles.list}>
					<Skeleton className={styles.skeleton} />
					<Skeleton className={styles.skeleton} />
					<Skeleton className={styles.skeleton} />
					<Skeleton className={styles.skeleton} />
					<Skeleton className={styles.skeleton} />
					<Skeleton className={styles.skeleton} />
				</div>
			</ScrollAreaViewport>
			<ScrollAreaBar forceMount orientation="horizontal" />
		</ScrollAreaRoot>
	);
};
